import { Suspense, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, ButtonGroup } from '@c2fo/liquidity';
import { QuestionCircleIcon } from '@c2fo/liquidity/icons';
import Skeleton from '@/enterprise/components/Skeleton';
import useBuyers, { type Buyer } from '@/findYourCustomer/data/useBuyers';
import moneyoutSvg from '@/shared/assets/moneyout.svg?url';
import percentagerotateSvg from '@/shared/assets/percentagerotate.svg?url';
import stopwatchSvg from '@/shared/assets/stopwatch.svg?url';
import { ButtonLink } from '@/shared/components/ButtonLink';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import BuyerLogo from '../BuyerLogo';
import CashFlowCalculationModal from './CashFlowCalculationModal';
import InstantPayCashFlowChart from './InstantPayCashFlowChart';
import {
  useInstantPayAvailableToday,
  useInstantPayCashFlowTotal,
  type CashFlowChartView,
} from './useInstantPayCashFlow';

const ValueSkeleton = ({ className }: { className: string }) => <Skeleton className={className} shadow={false} />;

// Curated cross-industry buyer set for the Instant Pay marketing story, independent of any given supplier's actual top buyers.
const TOP_CUSTOMER_BUYER_NAMES = [
  'Walmart',
  'Amazon',
  'Costco',
  'Kroger',
  'Albertsons Companies',
  'HP',
  'Sysco',
  'US Foods',
  'Best Buy',
  'McLane',
];

const normalizeBuyerName = (buyerName: string) => buyerName.trim().toLowerCase();

const useTopCustomerBuyers = () =>
  useBuyers((buyers) => {
    const buyersByName = new Map(buyers.map((buyer) => [normalizeBuyerName(buyer.buyerName), buyer]));
    const topBuyers = TOP_CUSTOMER_BUYER_NAMES.map((buyerName) =>
      buyersByName.get(normalizeBuyerName(buyerName))
    ).filter((buyer): buyer is Buyer => !!buyer);

    return { topBuyers, additionalCount: buyers.length - topBuyers.length };
  });

const TopCustomersCard = () => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();
  const { data } = useTopCustomerBuyers();
  const { topBuyers, additionalCount } = data;

  return (
    <div className="space-y-6 rounded-2xl bg-white p-6 shadow-sm">
      <div className="text-center text-lg font-medium">{t('instantPay.cashFlowStory.topCustomersDescription')}</div>
      <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
        {topBuyers.map((buyer) => (
          <BuyerLogo
            key={buyer.uuid}
            buyerLogoUrl={buyer.imagePng ?? buyer.imageSvg ?? buyer.buyerLogo}
            buyerName={buyer.buyerName}
            size="2xl"
            className="size-11 text-xl md:size-16 md:text-3xl"
          />
        ))}
      </div>
      {additionalCount > 0 && (
        <div className="text-center text-sm text-text-secondary">
          {t('instantPay.cashFlowStory.topCustomersAdditionalCount', {
            formattedCount: asNumber({ value: additionalCount, decimals: 0 }),
          })}
        </div>
      )}
    </div>
  );
};

// Hero banner numbers always reflect the Awarded AP view, regardless of the chart toggle below.
const CashFlowHeroDsoValue = () => {
  const { asPercent } = useLocaleFormat();
  const { awardedAp } = useInstantPayCashFlowTotal();

  return (
    <div className="w-full text-center font-serif text-3xl font-bold">
      {asPercent({ value: awardedAp.dsoPercent, decimals: 1 })}
    </div>
  );
};

// Placeholder: no data source yet for a true "days faster than Early Pay" figure, so this reuses the DSO improvement days.
const CashFlowHeroDaysFasterLabel = () => {
  const { asNumber } = useLocaleFormat();
  const { awardedAp } = useInstantPayCashFlowTotal();
  const dsoDays = Math.round(awardedAp.dsoDays);

  return (
    <Trans
      i18nKey="instantPay.cashFlowStory.availableTodayPlaceholderSubLabel"
      count={dsoDays}
      values={{ formattedCount: asNumber({ value: dsoDays, decimals: 0 }) }}
      components={{ bold: <span className="font-bold" /> }}
    />
  );
};

const CashFlowAvailableTodayValue = () => {
  const { asCurrencyCompact } = useLocaleFormat();
  const availableToday = useInstantPayAvailableToday();

  return <>{asCurrencyCompact(availableToday, 'USD')}</>;
};

interface CashFlowViewProps {
  view: CashFlowChartView;
}

const CashFlowEarlyPayCashFlowValue = ({ view }: CashFlowViewProps) => {
  const { asCurrencyCompact } = useLocaleFormat();
  const metrics = useInstantPayCashFlowTotal()[view];

  return <>{asCurrencyCompact(metrics.earlyPayCashFlow, 'USD')}</>;
};

const CashFlowAdditionalCashImpactValue = ({ view }: CashFlowViewProps) => {
  const { asCurrencyCompact } = useLocaleFormat();
  const metrics = useInstantPayCashFlowTotal()[view];

  return <>+ {asCurrencyCompact(metrics.additionalCashImpact, 'USD')}</>;
};

const CashFlowDsoImprovementValue = ({ view }: CashFlowViewProps) => {
  const { t } = useTranslation();
  const { asPercent, asNumber } = useLocaleFormat();
  const metrics = useInstantPayCashFlowTotal()[view];
  const dsoDays = Math.round(metrics.dsoDays);

  return (
    <>
      {asPercent({ value: metrics.dsoPercent, decimals: 1 })}{' '}
      <span className="text-2xl">
        ({t('core.daysWithCount', { count: dsoDays, formattedCount: asNumber({ value: dsoDays, decimals: 0 }) })})
      </span>
    </>
  );
};

const InstantPayCashFlowStory = () => {
  const { t } = useTranslation();
  const [cashFlowView, setCashFlowView] = useState<CashFlowChartView>('awardedAp');
  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);

  const CASH_FLOW_VIEW_OPTIONS: { label: string; value: CashFlowChartView }[] = [
    { label: t('instantPay.cashFlowStory.viewTotalEligibleAp'), value: 'totalEligibleAp' },
    { label: t('instantPay.cashFlowStory.viewAwardedAp'), value: 'awardedAp' },
  ];

  return (
    <div className="space-y-16">
      {/* Hero banner */}
      <div className="flex flex-col justify-between gap-8 rounded-2xl bg-secondary-500 p-6 text-white shadow-md md:p-14 xl:flex-row xl:items-center">
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="text-5xl font-normal leading-tight tracking-tight">
              {t('instantPay.cashFlowStory.headline')}
            </div>
            <div className="text-2xl">
              <Trans
                i18nKey="instantPay.cashFlowStory.subheadline"
                components={{ style: <span className="font-bold" /> }}
              />
            </div>
          </div>
          <div className="text-lg font-semibold">{t('instantPay.cashFlowStory.cta')}</div>
          <div>
            <ButtonLink
              to={asEnterpriseLink('instant-pay/activation')}
              variant="primary"
              inverse
              className="md:w-fit"
              size="lg"
            >
              {t('instantPay.cashFlowStory.upgradeButton')}
            </ButtonLink>
          </div>
        </div>
        <div className="shrink-0 rounded-xl bg-white/10 p-4 md:w-[440px]">
          <div>
            <div className="text-center text-2xl font-semibold">{t('instantPay.cashFlowStory.advantageTitle')}</div>
            <div className="text-center text-xs italic text-white">
              {t('instantPay.cashFlowStory.advantageSubtitle')}
            </div>
          </div>
          <div className="mt-2 overflow-hidden rounded-xl bg-white/10">
            <div className="flex items-start justify-center gap-4 p-4">
              <div className="flex flex-col items-center gap-1 text-center">
                <div>{t('instantPay.cashFlowStory.heroDsoLabel')}</div>
                <Suspense fallback={<ValueSkeleton className="mx-auto h-10 w-full max-w-28" />}>
                  <CashFlowHeroDsoValue />
                </Suspense>
              </div>
              <div className="w-px self-stretch bg-white/50" />
              <div className="flex flex-col items-center gap-1 text-center">
                <div>{t('instantPay.cashFlowStory.availableTodayLabel')}</div>
                <div className="w-full text-center font-serif text-3xl font-bold">
                  <Suspense fallback={<ValueSkeleton className="mx-auto h-10 w-full max-w-28" />}>
                    <CashFlowAvailableTodayValue />
                  </Suspense>
                </div>
              </div>
            </div>
            <div className="bg-secondary-500 px-4 py-2 text-center text-sm italic">
              <Suspense fallback={<ValueSkeleton className="mx-auto h-4 w-40" />}>
                <CashFlowHeroDaysFasterLabel />
              </Suspense>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between rounded-lg bg-white/60 p-4">
            <div className="text-sm text-secondary-500">{t('instantPay.cashFlowStory.annualizedImpactLabel')}</div>
            <div className="font-serif text-xl font-bold text-secondary-500">
              <Suspense fallback={<ValueSkeleton className="h-6 w-20" />}>
                <CashFlowAdditionalCashImpactValue view="awardedAp" />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
      {/* Key Benefits */}
      <div className="space-y-6">
        <div className="text-center text-3xl">{t('instantPay.cashFlowStory.keyBenefitsTitle')}</div>
        <div className="flex flex-col gap-6 md:flex-row md:items-stretch">
          <div className="flex flex-1 flex-col items-center space-y-2 rounded-xl bg-white p-6 text-center shadow-sm">
            <div className="flex shrink-0 justify-center">
              <img src={stopwatchSvg} alt="" className="size-24" />
            </div>
            <div className="text-lg font-bold">{t('instantPay.cashFlowStory.accelerateCashFlowTitle')}</div>
            <div>{t('instantPay.cashFlowStory.accelerateCashFlowDescription')}</div>
          </div>
          <div className="flex flex-1 flex-col items-center space-y-2 rounded-xl bg-white p-6 text-center shadow-sm">
            <div className="flex shrink-0 justify-center">
              <img src={moneyoutSvg} alt="" className="size-24" />
            </div>
            <div className="text-lg font-bold">{t('instantPay.cashFlowStory.strengthenResiliencyTitle')}</div>
            <div>{t('instantPay.cashFlowStory.strengthenResiliencyDescription')}</div>
          </div>
          <div className="flex flex-1 flex-col items-center space-y-2 rounded-xl bg-white p-6 text-center shadow-sm">
            <div className="flex shrink-0 justify-center">
              <img src={percentagerotateSvg} alt="" className="size-24" />
            </div>
            <div className="text-lg font-bold">{t('instantPay.cashFlowStory.reduceDsoTitle')}</div>
            <div>{t('instantPay.cashFlowStory.reduceDsoDescription')}</div>
          </div>
        </div>
      </div>
      {/* Top Customers */}
      <div className="space-y-6">
        <div className="text-center text-3xl">{t('instantPay.cashFlowStory.topCustomersTitle')}</div>
        <Suspense fallback={<Skeleton height="h-48" />}>
          <TopCustomersCard />
        </Suspense>
      </div>
      {/* Instant Pay Benefits */}
      <div className="space-y-6">
        <div className="space-y-1">
          <div className="text-center text-3xl">{t('instantPay.cashFlowStory.annualizedImpactTitle')}</div>
          <div className="text-center text-sm text-text-secondary">
            {t('instantPay.cashFlowStory.annualizedImpactSubtitle')}
          </div>
        </div>
        <div className="overflow-hidden rounded-xl bg-white shadow-sm">
          {/* Card header */}
          <div className="flex flex-col gap-4 bg-gray-100 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <div className="text-lg font-semibold">{t('instantPay.cashFlowStory.cashFlowStoryTitle')}</div>
            </div>
            <div className="flex shrink-0 flex-col space-y-2 md:items-end">
              <ButtonGroup options={CASH_FLOW_VIEW_OPTIONS} value={cashFlowView} onChange={setCashFlowView} />
              <Button
                className="w-fit [&>span:first-of-type]:shrink-0"
                icon={<QuestionCircleIcon className="fill-blue-500" />}
                size="sm"
                variant="link"
                onClick={() => setIsCalculationModalOpen(true)}
              >
                {t('instantPay.cashFlowStory.howCalculatedButton')}
              </Button>
            </div>
          </div>
          {/* Card body: summary cards + chart */}
          <div className="flex flex-col gap-6 p-6 xl:flex-row xl:items-stretch">
            <div className="flex w-full flex-col gap-3 xl:w-1/3">
              {/* Early Pay Cash on Balance Sheet Benefit */}
              <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border-[0.5px] border-stroke bg-white px-5 py-6 shadow-sm">
                <div className="text-center text-sm text-text-secondary">
                  {t('instantPay.cashFlowStory.earlyPayCashFlowLabel')}
                </div>
                <div className="w-full text-center font-serif text-3xl font-bold">
                  <Suspense fallback={<ValueSkeleton className="mx-auto h-10 w-full max-w-28" />}>
                    <CashFlowEarlyPayCashFlowValue view={cashFlowView} />
                  </Suspense>
                </div>
              </div>
              {/* Instant Pay Cash on Balance Sheet Benefit */}
              <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border-[0.5px] border-green-600 bg-green-100 px-5 py-6 shadow-sm">
                <div className="text-center text-sm text-text-secondary">
                  {t('instantPay.cashFlowStory.additionalCashImpactLabel')}
                </div>
                <div className="w-full text-center font-serif text-3xl font-bold text-green-800">
                  <Suspense fallback={<ValueSkeleton className="mx-auto h-10 w-full max-w-28" />}>
                    <CashFlowAdditionalCashImpactValue view={cashFlowView} />
                  </Suspense>
                </div>
              </div>
              {/* DSO Improvement */}
              <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border-[0.5px] border-teal-600 bg-teal-100 px-5 py-6 shadow-sm">
                <div className="text-center text-sm text-text-secondary">
                  {t('instantPay.cashFlowStory.dsoImprovementLabel')}
                </div>
                <div className="w-full text-center font-serif text-3xl font-bold text-teal-900">
                  <Suspense fallback={<ValueSkeleton className="mx-auto h-10 w-full max-w-28" />}>
                    <CashFlowDsoImprovementValue view={cashFlowView} />
                  </Suspense>
                </div>
              </div>
            </div>
            <div className="w-full overflow-x-auto xl:w-2/3">
              <div style={{ minWidth: 480 }}>
                <Suspense fallback={<Skeleton height="h-96" />}>
                  <InstantPayCashFlowChart view={cashFlowView} />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CashFlowCalculationModal open={isCalculationModalOpen} onClose={() => setIsCalculationModalOpen(false)} />
    </div>
  );
};

export default InstantPayCashFlowStory;
